import type { ContractStatusCode, ProjectContractStatus } from '../types';

/**
 * The project's status when it has several contracts (ClickUp 869fbrpjk): the
 * most urgent contract's status, the same for both parties, so "in progress"
 * never hides a contract that waits for a signature. The order and the
 * statuses come from the API (projects/project-contract-statuses.ts).
 */
export const CONTRACT_STATUS_ORDER: ContractStatusCode[] = [
  'dispute', 'conflict', 'disputed', 'under_resolution', 'signing', 'awaiting_payment',
  'payment_pending', 'ready_for_attestation', 'in_progress', 'not_started', 'closed'
];

/** Bootstrap colour per status, in line with the project status badges. */
export const CONTRACT_STATUS_COLOR: Record<ContractStatusCode, string> = {
  dispute: 'danger',
  conflict: 'danger',
  disputed: 'warning',
  under_resolution: 'warning',
  signing: 'warning',
  awaiting_payment: 'warning',
  payment_pending: 'info',
  ready_for_attestation: 'info',
  in_progress: 'primary',
  not_started: 'secondary',
  closed: 'success'
};

const rank = (status: ContractStatusCode) => CONTRACT_STATUS_ORDER.indexOf(status);

/** The contracts, most urgent first, then by contract number. */
export const sortedContractStatuses = (statuses: ProjectContractStatus[] = []) =>
  [...statuses].sort((left, right) => rank(left.status) - rank(right.status) || left.contractNumber - right.contractNumber);

/**
 * With two or more contracts: the status the project shows and how many other
 * contracts are in another state (the "+N"). Null with fewer contracts, where
 * the project keeps its usual status.
 */
export function strongestContractStatus(statuses: ProjectContractStatus[] = []) {
  if (statuses.length < 2) return null;
  const [strongest] = sortedContractStatuses(statuses);
  return {
    status: strongest.status,
    others: statuses.filter(item => item.status !== strongest.status).length
  };
}

/**
 * With one contract the project status stays "not started" until a stage
 * starts, also while the contract waits for a signature or for the payment.
 * Then the contract's status says more: null when the project status stands.
 */
export function singleContractStatus(
  displayStatus: string,
  statuses: ProjectContractStatus[] = []
): ContractStatusCode | null {
  if (displayStatus !== 'not_started' || statuses.length !== 1) return null;
  const [only] = statuses;
  return only.status === 'not_started' ? null : only.status;
}
